import React from 'react';
import toast from 'react-hot-toast';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * bookingGate — THE one brain for the pre-launch booking lock (Jose 2026-10-13, "they go thru 1 brain").
 *
 * SLIK isn't the booking system until the Oct-19 cutover (the shop still runs on Fresha), so a SLIK
 * booking made before then would be orphaned. Instead of a hard wall, every surface lets clients walk
 * the FULL funnel (pick barber/time/service, build their family) and only the final CONFIRM is locked.
 *
 * Every surface that can confirm a booking — Book Now / Open Chairs → CompactBookingPage, Emergency,
 * Family (MultiFamilyBookingPage) — reads isBookingOpen() for the button state and routes its write
 * through guardBookingWrite(); the greyed message is <BookingLockBar/>. NO per-surface bespoke gate (it
 * drifts — same doctrine as the availability brain). Auto-lifts at BOOKING_LAUNCH.
 */
export const BOOKING_LAUNCH = new Date(2026, 9, 19, 0, 0, 0); // LIVE 2026-10-19 — booking opened on go-live night (gate now in the past = open for all clients)

// Staging is a sandbox (separate DB, Stripe TEST keys) — the Oct-19 launch lock doesn't apply there, so
// QA can walk the FULL pay + book flow before go-live. Prod stays gated until launch. (Jose 2026-10-15)
const IS_STAGING =
  (typeof process !== 'undefined' && /staging/i.test(process.env.REACT_APP_FIREBASE_PROJECT_ID || '')) ||
  (typeof window !== 'undefined' && /staging/i.test(window.location.hostname));

// ── OWNER KILL SWITCH ── publicConfig/bookingPause (toggled from staff Settings). When paused, booking
// stops everywhere (buttons lock + the lock bar/toast show the paused message). Kept in sync by the
// self-subscribing listener at the bottom; defaults to NOT paused on load/error so a legit booking is
// never blocked by accident.
let _bookingPaused = false;
let _bookingPauseMsg = '';
export const setBookingPaused = (paused: boolean, message?: string) => { _bookingPaused = !!paused; _bookingPauseMsg = message || ''; };
export const isBookingPaused = (): boolean => _bookingPaused;
export const bookingPauseMessage = (): string => _bookingPauseMsg;

export const isBookingOpen = (): boolean => !_bookingPaused && (IS_STAGING || Date.now() >= BOOKING_LAUNCH.getTime());

export const BOOKING_LOCK_SHORT = '🔒 Opens Oct 19';
export const BOOKING_LOCK_MESSAGE =
  "Everything's set — confirming unlocks when SLIK goes live October 19. Until then, please keep booking on Fresha.";

/** Fire the standard "not live yet" toast. */
export function bookingLockToast(): void {
  const paused = isBookingPaused();
  const msg = paused ? (bookingPauseMessage() || 'Online booking is paused — please call the shop to book.') : BOOKING_LOCK_MESSAGE;
  toast(msg, { icon: paused ? '⏸️' : '🗓️', duration: 6000 });
}

/** Write-path guard for UI handlers — call at the TOP of every booking-write handler. Returns true if
 *  the write may proceed; otherwise fires the toast and returns false, so no booking can slip through
 *  before launch even if the button's disabled state is bypassed. */
export function guardBookingWrite(): boolean {
  if (isBookingOpen()) return true;
  bookingLockToast();
  return false;
}

/** Data-layer backstop — call at the TOP of every service write method. Throws before launch so NO
 *  write path (known or future, UI or system-triggered) can create an orphaned booking pre-cutover. */
export function assertBookingLive(): void {
  if (!isBookingOpen()) throw new Error('SLIK booking opens October 19 — not live yet.');
}

/** The greyed "booking opens Oct 19" bar shown next to a locked Confirm button. Null once live. */
export function BookingLockBar(): JSX.Element | null {
  if (isBookingOpen()) return null;
  if (isBookingPaused()) {
    return (
      <div className="mb-3 rounded-xl bg-[rgb(255,247,235)] ring-1 ring-[rgb(240,200,120)] px-4 py-2.5 text-center">
        <div className="text-[rgb(146,64,14)] text-sm font-bold">⏸️ Online booking is paused</div>
        <div className="text-[rgb(146,64,14)] text-xs mt-0.5">{bookingPauseMessage() || "Please call the shop to book — we'll be right back."}</div>
      </div>
    );
  }
  return (
    <div className="mb-3 rounded-xl bg-[rgb(255,247,235)] ring-1 ring-[rgb(240,200,120)] px-4 py-2.5 text-center">
      <div className="text-[rgb(146,64,14)] text-sm font-bold">🔒 Booking opens October 19</div>
      <div className="text-[rgb(146,64,14)] text-xs mt-0.5">You can set everything up now — the Confirm button unlocks on launch day.</div>
    </div>
  );
}

// Self-subscribing singleton — keep the kill-switch flag in sync the moment any booking surface imports
// this brain. Default NOT paused on error so legit booking is never blocked by accident.
try {
  onSnapshot(doc(db, 'publicConfig', 'bookingPause'),
    (s) => { const d = s.data() as any; setBookingPaused(!!(d && d.paused), d?.message); },
    () => setBookingPaused(false));
} catch { /* ignore */ }
